/**
 * Data layer for `api-driven-report-pull-for-custom-bi-dashboards`. Thin typed fetch wrappers over /api/v1/api-driven-report-pull-for-custom-bi-dashboards.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ApiDrivenReportPullForCustomBiDashboardsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListApiDrivenReportPullForCustomBiDashboardsResponse {
	count: number;
	items: ApiDrivenReportPullForCustomBiDashboardsRow[];
}

export interface CreateApiDrivenReportPullForCustomBiDashboardsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/api-driven-report-pull-for-custom-bi-dashboards";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListApiDrivenReportPullForCustomBiDashboardsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListApiDrivenReportPullForCustomBiDashboardsResponse>(res);
}

export async function create(body: CreateApiDrivenReportPullForCustomBiDashboardsBody): Promise<ApiDrivenReportPullForCustomBiDashboardsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ApiDrivenReportPullForCustomBiDashboardsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const apiDrivenReportPullForCustomBiDashboardsQueryKeys = {
	all: ["api-driven-report-pull-for-custom-bi-dashboards"] as const,
	list: () => [...apiDrivenReportPullForCustomBiDashboardsQueryKeys.all] as const,
	detail: (id: string) => [...apiDrivenReportPullForCustomBiDashboardsQueryKeys.all, id] as const,
};
